import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { UserTask } from './user-task';
export { UserTask };
import { userTaskSchema } from '@schemas/business-objects/user-task';
import { RoleFormGroup } from './role-form-group';
import { TenantFormGroup } from './tenant-form-group';
import { TokenFormGroup } from './token-form-group';
import { UserFormGroup } from './user-form-group';
import { UserTaskHistoryFormArray } from './user-task-history-form-group';
import type { Id } from '../base-types';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceHistory } from './process-instance-history';
import type { RoleAssignment } from './role-assignment';

export class UserTaskFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    name: SchemaFormControl;
    userInterface: SchemaFormControl;
    status: SchemaFormControl;
    data: SchemaFormControl;
    completedAt: SchemaFormControl;
    loopIndex: SchemaFormControl;
    resultApplied: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    tokenId: SchemaFormControl;
    token: TokenFormGroup;
    roleId: SchemaFormControl;
    role: RoleFormGroup;
    userId: SchemaFormControl;
    user: UserFormGroup;
    userTaskHistories: UserTaskHistoryFormArray;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<UserTask>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(userTaskSchema, UserTask, data, options);
  }

  get object(): UserTask {
    return new UserTask(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'token':
        return new TokenFormGroup();
      case 'role':
        return new RoleFormGroup();
      case 'user':
        return new UserFormGroup();
      case 'userTaskHistories':
        return new UserTaskHistoryFormArray();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<UserTask[]> {
    return UserTask.find(filter);
  }

  static async findOne(filter?: any): Promise<UserTask | null> {
    return UserTask.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<UserTask> {
    return UserTask.findById(id, filter);
  }

  static async create(data: Partial<UserTask>): Promise<UserTask> {
    return UserTask.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<UserTask>,
  ): Promise<UserTask> {
    return UserTask.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return UserTask.deleteById(id);
  }

  async claim(userId?: Id): Promise<any> {
    const instance = new UserTask(this.getRawValue());
    return instance.claim(userId);
  }

  async complete(options: any): Promise<any> {
    const instance = new UserTask(this.getRawValue());
    return instance.complete(options);
  }

  async reassign(userId: Id): Promise<any> {
    const instance = new UserTask(this.getRawValue());
    return instance.reassign(userId);
  }

  async unclaim(): Promise<any> {
    const instance = new UserTask(this.getRawValue());
    return instance.unclaim();
  }

  async updateData(options: any): Promise<any> {
    const instance = new UserTask(this.getRawValue());
    return instance.updateData(options);
  }
}

export class UserTaskFormArray extends PersistedFormArray {
  declare controls: UserTaskFormGroup[];

  readonly entityName = 'UserTask' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(userTaskSchema, UserTask, options);
  }

  protected override createItemGroup() {
    return new UserTaskFormGroup();
  }

  get array(): UserTask[] {
    return this.controls.map((group: UserTaskFormGroup) => group.object);
  }
}

export class UserTaskPersistedArray extends PersistedArray<UserTask> {
  readonly entityName = 'UserTask' as const;

  constructor(options?: PersistedArrayOptions) {
    super(UserTask, options);
  }
}
